/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { GlobalMarketData, MarketToken } from '../../types/market';
import { getGlobalMarketData } from '../../lib/coinmarketcap';
import { fetchRecentMetadata } from '../../lib/pinata';
import { raydiumApi } from '../raydium';
import { bondingCurveDB } from '../../lib/bonding-curve-db';
import { MARKET_DATA } from '../../data/market-data';

export const marketApi = {
    /**
     * Get Global Crypto Stats (MCap, Vol, BTC Dominance)
     */
    getGlobalStats: async (): Promise<GlobalMarketData> => {
        return await getGlobalMarketData();
    },

    /**
     * Get aggregated list of tokens for the terminal.
     * Merges:
     * 1. Live Raydium Pools (if Mainnet)
     * 2. Local/Recent User Mints (Pinned to IPFS)
     * 3. Static Demo Data
     */
    getTokens: async (chain: 'SOL' | 'SUI', network: 'mainnet' | 'devnet'): Promise<MarketToken[]> => {
        const isMainnet = network === 'mainnet';
        
        // 1. Fetch Parallel Data Sources
        const [raydiumPools, { tokens: recentTokens }] = await Promise.all([
            (isMainnet && chain === 'SOL') ? raydiumApi.getPools() : Promise.resolve([]),
            fetchRecentMetadata(0, 100, isMainnet)
        ]);

        // 2. Normalize Raydium Pools
        const raydiumMarketTokens: MarketToken[] = raydiumPools.map((pool, i) => ({
            id: pool.baseMint,
            name: pool.name.split('-')[0], 
            symbol: pool.name.split('-')[0], 
            description: `Liquidity Pool on Raydium.`,
            image: `https://placehold.co/400x400/1a1a1a/ffffff?text=${pool.name.split('-')[0]}`,
            price: pool.price, 
            change24h: (Math.random() * 20) - 10, 
            volume24h: pool.volume24h, 
            marketCap: pool.liquidity * 2,
            launchDate: Date.now() - (100000000 * i), 
            isHot: pool.volume24h > 100000,
            priceHistory: Array.from({length: 20}, () => pool.price * (1 + (Math.random() - 0.5) * 0.1)),
            properties: { 
                chain: 'SOL', 
                mintAddress: pool.baseMint, 
                tokenStandard: 'SPL',
                isLive: true,
                aiAnalyticsEnabled: true,
            }
        }));

        // 3. Normalize Recent Mints (with Bonding Curve Simulation)
        const recentMarketTokens: MarketToken[] = recentTokens.map(t => {
            const curve = t.properties?.mintAddress ? bondingCurveDB.getCurve(t.properties.mintAddress) : null;
            // Fallback price if no curve state exists yet
            const price = curve ? curve.tokenPrice : 1.25; 
            
            return {
                ...t, 
                id: t.properties?.mintAddress || t.id, 
                price: price, 
                change24h: (Math.random() * 10) - 5,
                volume24h: curve ? curve.realSolReserves * 100 : 0,
                marketCap: curve ? curve.marketCap : 1250000, 
                launchDate: t.properties?.createdAt || Date.now(), 
                isHot: true,
                priceHistory: Array.from({length: 20}, () => price * (1 + (Math.random() - 0.5) * 0.1)),
            };
        });

        // 4. Normalize Static Data
        const staticTokens: MarketToken[] = MARKET_DATA.map(t => {
            const curve = t.properties?.mintAddress ? bondingCurveDB.getCurve(t.properties.mintAddress) : null;
            const price = curve ? curve.tokenPrice : t.price;
            return { 
                ...t, 
                price, 
                marketCap: curve ? curve.marketCap : t.marketCap,
                priceHistory: Array.from({length: 20}, () => price * (1 + (Math.random() - 0.5) * 0.1)),
            };
        });
        
        // 5. Merge & Deduplicate (Prefer Real > Recent > Static)
        const all = [...recentMarketTokens, ...staticTokens, ...raydiumMarketTokens];
        const unique = Array.from(new Map(all.map(item => [item.id, item])).values());
        
        return unique;
    }
};